import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from '../../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import {
  ArrowLeft, FileText, Loader2, Search, CheckCircle2, AlertTriangle, Clock,
  DollarSign, Ban, Pencil, FileMinus, Copy, History, XCircle, Link as LinkIcon, Eye,
  Banknote, StickyNote, Wallet, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

const statusLabels = {
  paid: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', text: 'Paid', Icon: CheckCircle2 },
  unpaid: { cls: 'bg-sky-100 text-sky-700 border-sky-200', text: 'Unpaid', Icon: Clock },
  pending: { cls: 'bg-amber-100 text-amber-700 border-amber-200', text: 'Pending', Icon: Clock },
  overdue: { cls: 'bg-red-100 text-red-700 border-red-200', text: 'Overdue', Icon: AlertTriangle },
  cancelled: { cls: 'bg-gray-100 text-gray-600 border-gray-200', text: 'Voided', Icon: XCircle },
};

const daysBetween = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = Math.round((d - new Date()) / 86400000);
  return diff;
};

const intelligenceLabel = (inv) => {
  const s = (inv.payment_status || '').toLowerCase();
  if (s === 'cancelled') return 'Voided';
  if (s === 'paid') return 'Paid in full';
  if (inv.payment_structure === 'deposit' && inv.deposit_paid) {
    const days = daysBetween(inv.final_payment_due_date);
    if (days !== null) {
      if (days < 0) return `Balance overdue by ${Math.abs(days)}d`;
      if (days === 0) return 'Balance due today';
      return `Balance due in ${days}d`;
    }
    return 'Balance pending';
  }
  if (inv.payment_structure === 'deposit') {
    const days = daysBetween(inv.due_date);
    if (days !== null) {
      if (days < 0) return `Deposit overdue by ${Math.abs(days)}d`;
      return `Deposit due`;
    }
  }
  if ((inv.payment_terms || '') === 'net30') {
    const days = daysBetween(inv.due_date);
    if (days !== null) {
      if (days < 0) return `Net 30 overdue by ${Math.abs(days)}d`;
      return `Net 30 — due in ${days}d`;
    }
  }
  const days = daysBetween(inv.due_date);
  if (days !== null) {
    if (days < 0) return `Overdue by ${Math.abs(days)}d`;
    if (days === 0) return 'Due today';
    return `Due in ${days}d`;
  }
  return '';
};

// Iter-312 — Event traceability line for admin invoice list rows.
// Extracted into its own component so the babel visual-editor plugin
// treats it as a single JSX unit (nested logical expressions inside
// the parent JSX were being stripped by the plugin).
const EventTraceLine = ({ inv }) => {
  const parts = [];
  if (inv.event_title) parts.push(inv.event_title);
  if (inv.venue_name) parts.push(inv.venue_name);
  const start = inv.event_date_start || inv.gig_date;
  const end = inv.event_date_end || inv.gig_date;
  if (start) {
    parts.push(start && end && start !== end
      ? `${formatDateMMDDYYYY(start)} → ${formatDateMMDDYYYY(end)}`
      : formatDateMMDDYYYY(start));
  }
  if (parts.length === 0) return null;
  return (
    <p className="text-xs text-gray-700 mt-1 font-medium" data-testid={`admin-invoice-event-line-${inv.id}`}>
      {inv.event_title ? <span className="text-cyan-800">{parts[0]}</span> : parts[0]}
      {parts.slice(1).map((p, i) => (
        <span key={i}><span className="text-gray-400"> · </span>{p}</span>
      ))}
    </p>
  );
};

const AdminInvoices = () => {
  const navigate = useNavigate();
  const { invoiceId: urlInvoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const prefilterClient = searchParams.get('client_id') || '';
  const prefilterStatus = searchParams.get('filter') || '';
  const prefilterBucket = searchParams.get('bucket') || '';   // d0_30 | d31_60 | d61_90 | d90_plus
  const prefilterStale  = searchParams.get('stale') === '1';

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState(prefilterStatus && ['unpaid','pending','overdue','paid','cancelled'].includes(prefilterStatus) ? prefilterStatus : 'all');
  const [search, setSearch] = useState('');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [auditLog, setAuditLog] = useState([]);

  // Action modals
  const [voidDialog, setVoidDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [editDialog, setEditDialog] = useState(false);
  const [editLineItems, setEditLineItems] = useState([]);
  const [editReason, setEditReason] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [creditDialog, setCreditDialog] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditNotes, setCreditNotes] = useState('');
  // Iter-223b — Mark Paid Externally dialog (Check / Wire / ACH / Cash / Other)
  const [extDialog, setExtDialog] = useState(false);
  const [extMethod, setExtMethod] = useState('check');
  const [extAmount, setExtAmount] = useState('');
  const [extReference, setExtReference] = useState('');
  const [extPaidOn, setExtPaidOn] = useState('');
  const [extNotes, setExtNotes] = useState('');
  // Iter-223c — Add Note dialog
  const [noteDialog, setNoteDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  // Iter-327 — Universal Payment Link (guest-friendly)
  const [guestLinkDialog, setGuestLinkDialog] = useState(false);
  const [guestLink, setGuestLink] = useState(null);       // {status, url, token, expires_at, ...}
  const [guestLinkLoading, setGuestLinkLoading] = useState(false);
  const [genExpiryDays, setGenExpiryDays] = useState(30);
  const [genPassFees, setGenPassFees] = useState(false);
  const [genNoExpiry, setGenNoExpiry] = useState(false);
  const [sendChannel, setSendChannel] = useState('email');
  const [sendTo, setSendTo] = useState('');
  const [sendGuestName, setSendGuestName] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sendBusy, setSendBusy] = useState(false);

  // Iter-328 Phase 1 — Mark funding received
  const [fundingDialog, setFundingDialog] = useState(false);
  const [fundingAmount, setFundingAmount] = useState('');
  const [fundingReference, setFundingReference] = useState('');
  const [fundingNotes, setFundingNotes] = useState('');
  const [fundingBusy, setFundingBusy] = useState(false);
  const [batchResult, setBatchResult] = useState(null);

  // Iter-227g — Bulk Mark-as-Paid-Externally multi-select
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkMethod, setBulkMethod] = useState('ach');
  const [bulkReference, setBulkReference] = useState('');
  const [bulkPaidOn, setBulkPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => { setSelectedIds(new Set()); setBulkMode(false); };
  const selectableInvoices = (list) =>
    list.filter((inv) => {
      const s = (inv.payment_status || 'unpaid').toLowerCase();
      // Only invoices with outstanding balance can be settled externally.
      return s !== 'paid' && s !== 'cancelled' && parseFloat(inv.amount_due || 0) > 0.005;
    });

  // Iter-223 — Invoice dashboard filter chips. Maps to backend `status`
  // query param values: paid_other_way / partial / future_open / overdue_dynamic.
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = prefilterClient ? { client_id: prefilterClient } : {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/invoicing/list', { params });
      setInvoices(res.data?.invoices || []);
    } catch {
      try {
        const res = await api.get('/invoices/admin', { params: prefilterClient ? { client_id: prefilterClient } : {} });
        setInvoices(res.data || []);
      } catch { /* noop */ }
    } finally {
      setLoading(false);
    }
  }, [prefilterClient, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/invoicing/${id}`);
      const data = res.data || {};
      const flat = data.invoice ? { ...data.invoice, line_items: data.line_items || [], payments: data.payments || [] } : data;
      setDetail(flat);
      // Load audit
      try {
        const a = await api.get(`/invoicing/${id}/audit`);
        setAuditLog(a.data || []);
      } catch { setAuditLog([]); }
    } catch {
      setDetail(null);
      setAuditLog([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (urlInvoiceId) loadDetail(urlInvoiceId);
    else setDetail(null);
  }, [urlInvoiceId, loadDetail]);

  const closeDetail = () => {
    setDetail(null);
    setAuditLog([]);
    if (urlInvoiceId) navigate('/admin/invoices', { replace: true });
  };

  const canVoid = detail && !['paid', 'cancelled'].includes((detail.payment_status || '').toLowerCase());
  const canEdit = canVoid;
  const canCredit = detail && (detail.payment_status || '').toLowerCase() === 'paid' && !detail.is_credit_note;
  const canPay = canVoid && parseFloat(detail?.amount_due || 0) > 0;

  // ── Void
  const openVoid = () => { setVoidReason(''); setVoidDialog(true); };
  const submitVoid = async () => {
    if (voidReason.trim().length < 3) { toast.error('Please provide a reason (min 3 chars).'); return; }
    setActionBusy(true);
    try {
      const res = await api.post(`/invoicing/${detail.id}/void`, { reason: voidReason });
      toast.success(`Invoice voided (${res.data.voided_count} total).`);
      setVoidDialog(false);
      await loadDetail(detail.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    } finally { setActionBusy(false); }
  };

  // ── Edit
  const openEdit = () => {
    setEditLineItems(
      (detail.line_items || []).map((li) => ({
        description: li.description || '',
        quantity: li.quantity ?? 1,
        unit_price: li.unit_price ?? 0,
        role_name: li.role_name || '',
      })),
    );
    setEditReason('');
    setEditNotes(detail.notes || '');
    setEditDialog(true);
  };
  const addLineItem = () => setEditLineItems((l) => [...l, { description: '', quantity: 1, unit_price: 0, role_name: '' }]);
  const removeLineItem = (i) => setEditLineItems((l) => l.filter((_, idx) => idx !== i));
  const updateLineItem = (i, key, val) => setEditLineItems((l) => l.map((li, idx) => idx === i ? { ...li, [key]: val } : li));
  const editTotal = editLineItems.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0), 0);
  const submitEdit = async () => {
    if (editReason.trim().length < 3) { toast.error('Please provide a reason (min 3 chars).'); return; }
    if (editLineItems.length === 0) { toast.error('At least one line item required.'); return; }
    setActionBusy(true);
    try {
      const payload = {
        line_items: editLineItems.map((li) => ({
          description: li.description,
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
          role_name: li.role_name || null,
        })),
        notes: editNotes,
        reason: editReason,
      };
      const res = await api.patch(`/invoicing/${detail.id}/adjust`, payload);
      toast.success(`Invoice adjusted. New total: $${res.data.new_total_amount.toFixed(2)}`);
      setEditDialog(false);
      await loadDetail(detail.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Adjust failed');
    } finally { setActionBusy(false); }
  };

  // ── Credit Note
  const openCredit = () => { setCreditAmount(''); setCreditReason(''); setCreditNotes(''); setCreditDialog(true); };
  const submitCredit = async () => {
    const amt = parseFloat(creditAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount.'); return; }
    if (creditReason.trim().length < 3) { toast.error('Please provide a reason.'); return; }
    setActionBusy(true);
    try {
      const res = await api.post(`/invoicing/${detail.id}/credit-note`, {
        amount: amt,
        reason: creditReason,
        notes: creditNotes || null,
      });
      toast.success(`Credit note ${res.data.credit_note_number} issued for $${amt.toFixed(2)}.`);
      setCreditDialog(false);
      await loadDetail(detail.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Credit note failed');
    } finally { setActionBusy(false); }
  };

  // ── Copy Payment Link
  const copyPaymentLink = async () => {
    setActionBusy(true);
    try {
      const res = await api.post(`/invoicing/${detail.id}/payment-link`, { origin_url: window.location.origin });
      const url = res.data.url;
      await navigator.clipboard.writeText(url);
      toast.success(`Payment link copied (${url.slice(0, 40)}…)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate payment link');
    } finally { setActionBusy(false); }
  };

  // ── Iter-327: Universal (guest) Payment Link
  const openGuestLink = async () => {
    if (!detail) return;
    setGuestLinkDialog(true);
    setGuestLinkLoading(true);
    setGuestLink(null);
    setGenExpiryDays(30);
    setGenPassFees(false);
    setGenNoExpiry(false);
    setSendChannel('email');
    setSendTo(detail?.client_email || '');
    setSendGuestName('');
    setSendMessage('');
    try {
      const res = await api.get(`/invoices/${detail.id}/payment-link`);
      setGuestLink(res.data);
    } catch (e) {
      // if invoice has no link yet the endpoint still returns status='not_generated'
      setGuestLink(null);
      toast.error(e.response?.data?.detail || 'Could not load link');
    } finally {
      setGuestLinkLoading(false);
    }
  };
  const generateGuestLink = async () => {
    setGuestLinkLoading(true);
    try {
      const res = await api.post(`/invoices/${detail.id}/payment-link/generate`, {
        expires_in_days: genExpiryDays,
        pass_fees: genPassFees,
        no_expiry: genNoExpiry,
      });
      setGuestLink(res.data);
      toast.success('Payment link generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate link');
    } finally { setGuestLinkLoading(false); }
  };
  const regenerateGuestLink = async () => {
    if (!window.confirm('Regenerate the link? The current link will stop working immediately.')) return;
    await generateGuestLink();
  };
  const extendGuestLink = async () => {
    const raw = window.prompt('Extend expiry by how many days? (Leave blank for "never expires")', '30');
    if (raw === null) return; // cancelled
    const noExpiry = raw.trim() === '';
    const days = noExpiry ? null : parseInt(raw, 10);
    if (!noExpiry && (isNaN(days) || days < 1 || days > 365)) {
      toast.error('Enter a number of days between 1 and 365, or leave blank for never');
      return;
    }
    setGuestLinkLoading(true);
    try {
      const res = await api.post(`/invoices/${detail.id}/payment-link/extend`, {
        expires_in_days: noExpiry ? null : days,
        no_expiry: noExpiry,
      });
      setGuestLink(res.data);
      toast.success(noExpiry ? 'Link no longer expires' : `Link extended by ${days} days`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extend failed');
    } finally { setGuestLinkLoading(false); }
  };
  const disableGuestLink = async () => {
    if (!window.confirm('Disable this payment link? Recipients will get a 410 error.')) return;
    setGuestLinkLoading(true);
    try {
      const res = await api.post(`/invoices/${detail.id}/payment-link/disable`, {});
      setGuestLink(res.data);
      toast.success('Payment link disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not disable link');
    } finally { setGuestLinkLoading(false); }
  };
  const copyGuestLink = async () => {
    if (!guestLink?.url) return;
    await navigator.clipboard.writeText(guestLink.url);
    toast.success('Link copied to clipboard');
  };
  const sendGuestLink = async () => {
    const to = (sendTo || '').trim();
    if (!to) { toast.error(sendChannel === 'email' ? 'Enter an email' : 'Enter a phone number'); return; }
    setSendBusy(true);
    try {
      await api.post(`/invoices/${detail.id}/payment-link/send`, {
        channel: sendChannel,
        to,
        message: sendMessage || null,
        guest_name: sendGuestName || null,
      });
      toast.success(sendChannel === 'email' ? `Email sent to ${to}` : `Text sent to ${to}`);
      // refresh last_sent_at
      const res = await api.get(`/invoices/${detail.id}/payment-link`);
      setGuestLink(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSendBusy(false); }
  };

  // Iter-328 Phase 1 — Mark funding received + auto-build batch
  const openFunding = () => {
    setFundingAmount(String(detail?.total_amount || ''));
    setFundingReference('');
    setFundingNotes('');
    setBatchResult(null);
    setFundingDialog(true);
  };
  const submitFunding = async () => {
    const amt = parseFloat(fundingAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid deposit amount'); return; }
    setFundingBusy(true);
    try {
      await api.post(`/admin/invoices/${detail.id}/mark-funding-received`, {
        amount: amt,
        reference: fundingReference || null,
        notes: fundingNotes || null,
      });
      toast.success('Funding recorded. Building payment batch…');
      const batchRes = await api.post(`/admin/staff-payments/batches/from-invoice/${detail.id}`);
      setBatchResult(batchRes.data);
      toast.success(`Batch created: ${batchRes.data.line_count || 0} talent lines · ${(batchRes.data.total_gross || 0).toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Funding step failed');
    } finally { setFundingBusy(false); }
  };

  // Iter-227g — Submit bulk Mark-as-Paid-Externally
  const submitBulk = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('Pick at least one invoice'); return; }
    if (!bulkMethod) { toast.error('Pick a payment method'); return; }
    if (!bulkPaidOn) { toast.error('Pick a payment date'); return; }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const res = await api.post('/invoicing/bulk-mark-paid-externally', {
        invoice_ids: ids,
        payment_method: bulkMethod,
        reference_number: bulkReference || null,
        paid_on: bulkPaidOn,
        admin_notes: bulkNotes || null,
        settle_full_balance: true,
      });
      setBulkResult(res.data);
      const { succeeded, failed, total_settled } = res.data;
      if (failed === 0) {
        toast.success(`Settled ${succeeded} invoice(s) — $${total_settled.toFixed(2)} total`);
      } else {
        toast.warning(`${succeeded} settled, ${failed} failed — see details below`);
      }
      await load();
    } catch (e) {
      // Iter-227g — Pydantic 422 returns `detail` as a list of objects which
      // would crash sonner's toast / React render. Always coerce to string.
      const raw = e.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((r) => r.msg || JSON.stringify(r)).join('; ')
        : (typeof raw === 'string' ? raw : 'Bulk update failed');
      toast.error(msg);
    } finally { setBulkBusy(false); }
  };

  // ── Mark Paid Externally (Iter-223b) — Check / Wire / ACH / Cash / Other
  const openExternal = () => {
    const due = parseFloat(detail?.amount_due || 0);
    setExtMethod('check');
    setExtAmount(due > 0 ? due.toFixed(2) : '');
    setExtReference('');
    setExtPaidOn(new Date().toISOString().slice(0, 10));
    setExtNotes('');
    setExtDialog(true);
  };
  const submitExternal = async () => {
    const amt = parseFloat(extAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount.'); return; }
    if (!extMethod) { toast.error('Select a payment method.'); return; }
    setActionBusy(true);
    try {
      const res = await api.post(`/invoicing/${detail.id}/mark-paid-externally`, {
        amount: amt,
        payment_method: extMethod,
        reference_number: extReference || null,
        paid_on: extPaidOn || null,
        admin_notes: extNotes || null,
      });
      toast.success(
        res.data.fully_paid
          ? `Marked paid in full via ${extMethod}.`
          : `Recorded ${extMethod} payment of $${amt.toFixed(2)}. Balance: $${res.data.amount_due.toFixed(2)}.`,
      );
      setExtDialog(false);
      await loadDetail(detail.id);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'External payment failed');
    } finally { setActionBusy(false); }
  };

  // ── Add Note (Iter-223c) — append internal admin note
  const openNote = () => { setNoteText(''); setNoteDialog(true); };
  const submitNote = async () => {
    const txt = noteText.trim();
    if (txt.length < 1) { toast.error('Enter a note.'); return; }
    setActionBusy(true);
    try {
      await api.post(`/invoicing/${detail.id}/add-note`, { note: txt });
      toast.success('Note added to invoice audit log.');
      setNoteDialog(false);
      await loadDetail(detail.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add note');
    } finally { setActionBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysOverdue = (i) => {
      if (!i.due_date) return 0;
      const d = new Date(i.due_date);
      d.setHours(0, 0, 0, 0);
      return Math.floor((today - d) / 86400000);
    };
    const isEffectivelyOverdue = (i) => {
      const s = (i.payment_status || i.status || '').toLowerCase();
      if (['paid', 'cancelled', 'void'].includes(s)) return false;
      return daysOverdue(i) > 0;
    };
    const bucketMatch = (i) => {
      const d = daysOverdue(i);
      switch (prefilterBucket) {
        case 'd0_30':    return d >= 1 && d <= 30;
        case 'd31_60':   return d >= 31 && d <= 60;
        case 'd61_90':   return d >= 61 && d <= 90;
        case 'd90_plus': return d > 90;
        default:         return true;
      }
    };
    const staleMatch = (i) => {
      if (!prefilterStale) return true;
      // Stale = overdue with no recent reminder activity
      if (!isEffectivelyOverdue(i)) return false;
      if (!i.last_reminder_sent_at) return true;
      const last = new Date(i.last_reminder_sent_at);
      const diff = (Date.now() - last.getTime()) / 86400000;
      return diff >= 7;
    };
    return invoices.filter((i) => {
      const s = (i.payment_status || i.status || '').toLowerCase();
      if (filter === 'overdue') {
        if (!(isEffectivelyOverdue(i) || s === 'overdue')) return false;
      } else if (filter !== 'all' && filter !== s) {
        return false;
      }
      if (!bucketMatch(i)) return false;
      if (!staleMatch(i)) return false;
      if (!q) return true;
      const hay = [i.invoice_number, i.client_name, i.client_company, i.client_email, i.venue_name]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [invoices, filter, search, prefilterBucket, prefilterStale]);

  const totals = useMemo(() => {
    let due = 0, paid = 0, overdue = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    invoices.forEach((i) => {
      const s = (i.payment_status || i.status || '').toLowerCase();
      if (['cancelled', 'void'].includes(s)) return;
      const amt = parseFloat(i.amount_due || i.total_amount || 0);
      if (s === 'paid') {
        paid += parseFloat(i.amount_paid || i.paid_amount || 0);
        return;
      }
      // Effective overdue: due_date in the past, regardless of literal payment_status label
      const isOverdueByDate = !!i.due_date && (() => {
        const d = new Date(i.due_date);
        d.setHours(0, 0, 0, 0);
        return d < today;
      })();
      if (s === 'overdue' || isOverdueByDate) overdue += amt;
      else due += amt;
    });
    return { due, paid, overdue };
  }, [invoices]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/20 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="admin-invoices-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-cyan-500" /> Invoices
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">All client invoices — auto-generated on booking confirmation.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: 'Outstanding', val: totals.due, cls: 'text-sky-700', tid: 'admin-invoices-outstanding' },
            { label: 'Overdue', val: totals.overdue, cls: 'text-red-600', tid: 'admin-invoices-overdue' },
            { label: 'Collected', val: totals.paid, cls: 'text-emerald-700', tid: 'admin-invoices-collected' },
          ].map((c) => (
            <Card key={c.label} className="bg-white">
              <CardHeader className="pb-1 pt-3 px-4"><CardTitle className="text-xs text-gray-500">{c.label}</CardTitle></CardHeader>
              <CardContent className="pt-1 pb-3 px-4">
                <div className={`text-2xl font-bold ${c.cls}`} data-testid={c.tid}>${c.val.toFixed(2)}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search invoice #, client, venue…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="admin-invoices-search"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {['all', 'unpaid', 'pending', 'overdue', 'paid', 'cancelled'].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={filter === s ? 'default' : 'outline'}
                onClick={() => setFilter(s)}
                className={filter === s ? 'bg-cyan-500 hover:bg-cyan-600 text-white' : ''}
                data-testid={`admin-invoices-filter-${s}`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Iter-223 — AR-focused server-side filters (Paid Other Way,
            Partial, Future Open, Overdue Dynamic). These hit the backend
            list endpoint with the new `status` filter values so the
            counts/aging stay accurate. */}
        <div className="flex gap-1.5 flex-wrap" data-testid="ar-status-filter-row">
          <span className="text-xs uppercase tracking-wider text-gray-500 self-center mr-1">AR view:</span>
          {[
            { id: 'all', label: 'All' },
            { id: 'paid_other_way', label: 'Paid Other Way' },
            { id: 'partial', label: 'Partial Payments' },
            { id: 'future_open', label: 'Future Open' },
            { id: 'overdue_dynamic', label: 'Overdue' },
          ].map((opt) => (
            <Button
              key={opt.id}
              size="sm"
              variant={statusFilter === opt.id ? 'default' : 'outline'}
              onClick={() => setStatusFilter(opt.id)}
              className={statusFilter === opt.id ? 'bg-rose-500 hover:bg-rose-600 text-white' : ''}
              data-testid={`ar-status-filter-${opt.id}`}
            >
              {opt.label}
            </Button>
          ))}
          {/* Iter-227g — Bulk mode toggle */}
          <Button
            size="sm"
            variant={bulkMode ? 'default' : 'outline'}
            onClick={() => { setBulkMode((v) => !v); if (bulkMode) clearSelection(); }}
            className={bulkMode ? 'bg-cyan-600 hover:bg-cyan-700 text-white ml-auto' : 'ml-auto'}
            data-testid="bulk-mode-toggle"
          >
            {bulkMode ? 'Cancel multi-select' : 'Multi-select'}
          </Button>
        </div>

        {(prefilterBucket || prefilterStale) && (
          <div className="mb-3 flex items-center gap-2 text-xs" data-testid="bucket-filter-banner">
            <span className="px-2 py-1 rounded-md bg-amber-50 border border-amber-300 text-amber-800 font-medium">
              {prefilterStale ? 'Stale only' : `Aging: ${ ({ d0_30: '0–30 days', d31_60: '31–60 days', d61_90: '61–90 days', d90_plus: '90+ days' })[prefilterBucket] || prefilterBucket }`}
            </span>
            <button
              type="button"
              onClick={() => navigate('/admin/invoices')}
              className="text-gray-500 hover:text-gray-900 underline"
              data-testid="clear-bucket-filter"
            >
              Clear
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-sm text-gray-500">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" /> No invoices match.
          </CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0 divide-y divide-gray-100">
              {filtered.map((inv) => {
                const s = (inv.payment_status || inv.status || 'unpaid').toLowerCase();
                const L = statusLabels[s] || statusLabels.unpaid;
                const amountDue = parseFloat(inv.amount_due || inv.total_amount || 0);
                const intel = intelligenceLabel(inv);
                const voided = s === 'cancelled';
                const isSelectable = s !== 'paid' && !voided && amountDue > 0.005;
                const isSelected = selectedIds.has(inv.id);
                return (
                  <div
                    key={inv.id}
                    className={`w-full px-4 py-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${voided ? 'opacity-60' : ''} ${isSelected ? 'bg-cyan-50' : ''}`}
                    data-testid={`admin-invoice-row-${inv.id}`}
                  >
                    {bulkMode && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!isSelectable}
                        onChange={() => toggleSelected(inv.id)}
                        className="mt-1 w-4 h-4 accent-cyan-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                        data-testid={`bulk-select-${inv.id}`}
                        title={isSelectable ? '' : 'Already settled or voided'}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (bulkMode && isSelectable) toggleSelected(inv.id);
                        else if (!bulkMode) navigate(`/admin/invoices/${inv.id}`);
                      }}
                      className="text-left flex items-start justify-between gap-4 flex-1 min-w-0"
                    >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-semibold text-gray-900 ${voided ? 'line-through' : ''}`}>{inv.invoice_number}</p>
                        <Badge className={`text-[10px] border ${L.cls}`}>
                          <L.Icon className="w-3 h-3 mr-0.5" /> {L.text}
                        </Badge>
                        {inv.payment_structure === 'deposit' && (
                          <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200">
                            {inv.parent_invoice_id ? 'Balance' : 'Deposit'}
                          </Badge>
                        )}
                        {inv.is_credit_note && (
                          <Badge className="text-[10px] bg-purple-50 text-purple-700 border border-purple-200">Credit</Badge>
                        )}
                        {/* Iter-224f — Consolidation pill: "Consolidated · 3 Days · Fri–Sun" */}
                        {inv.consolidated_pill && (
                          <Badge
                            className="text-[10px] bg-cyan-50 text-cyan-700 border border-cyan-200"
                            data-testid={`admin-invoice-consolidated-pill-${inv.id}`}
                          >
                            <Layers className="w-3 h-3 mr-0.5" />
                            {inv.consolidated_pill.label}
                          </Badge>
                        )}
                        {intel && !voided && (
                          <span className="text-[10px] text-gray-500">· {intel}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {inv.client_company_name || inv.client_company || inv.client_contact_name || inv.client_name || inv.client_email || '—'}
                        {inv.client_company_name && inv.client_contact_name ? ` (${inv.client_contact_name})` : ''}
                      </p>
                      {/* Iter-312 — Event traceability line: event title + date + venue */}
                      <EventTraceLine inv={inv} />
                      {(inv.last_reminder_sent_at || inv.reminder_count) && (
                        <p className="text-[10px] text-gray-400 mt-0.5" data-testid={`invoice-activity-${inv.id}`}>
                          Last activity: {inv.last_reminder_kind ? `${inv.last_reminder_kind} ` : ''}reminder
                          {inv.last_reminder_sent_at ? ` · ${formatDateMMDDYYYY(inv.last_reminder_sent_at)}` : ''}
                          {inv.reminder_count ? ` · ${inv.reminder_count} sent total` : ''}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-semibold ${inv.is_credit_note ? 'text-purple-700' : 'text-gray-900'}`}>
                        ${(parseFloat(inv.total_amount || 0)).toFixed(2)}
                      </p>
                      {amountDue > 0 && s !== 'paid' && !voided && (
                        <p className="text-[11px] text-sky-700 flex items-center justify-end gap-0.5">
                          <DollarSign className="w-3 h-3" />{amountDue.toFixed(2)} due
                        </p>
                      )}
                    </div>
                    </button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Iter-227g — Sticky bulk action bar */}
        {bulkMode && selectedIds.size > 0 && (
          <div
            className="sticky bottom-2 mt-4 z-30 mx-auto max-w-3xl bg-white border-2 border-cyan-500 rounded-xl shadow-xl px-4 py-3 flex items-center gap-3 flex-wrap"
            data-testid="bulk-action-bar"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                {selectedIds.size} invoice{selectedIds.size === 1 ? '' : 's'} selected
              </p>
              <p className="text-xs text-gray-500">Shared payment method · reference · date</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              data-testid="bulk-clear-selection"
            >
              Clear
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                setBulkResult(null);
                setBulkDialog(true);
              }}
              data-testid="bulk-open-dialog"
            >
              <Banknote className="w-4 h-4 mr-1" />
              Mark as Paid Externally ({selectedIds.size})
            </Button>
          </div>
        )}
      </div>

      {/* ──────────────── Invoice Detail + Controls ──────────────── */}
      <Dialog open={!!urlInvoiceId} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent
          className="max-w-3xl w-[calc(100vw-1rem)] bg-white max-h-[92vh] overflow-y-auto p-4 sm:p-6"
          data-testid="admin-invoice-detail-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Invoice {detail?.invoice_number || ''}</span>
              {detail?.payment_status === 'cancelled' && (
                <Badge className="text-[10px] bg-gray-100 text-gray-600 border border-gray-200">VOIDED</Badge>
              )}
            </DialogTitle>
          <DialogDescription className="sr-only">Invoice</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
          ) : !detail ? (
            <p className="text-sm text-gray-500 py-8 text-center">Invoice not found.</p>
          ) : (
            <Tabs defaultValue="overview">
              <TabsList className="mb-3">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="payments" data-testid="tab-payments">
                  Payments ({(detail.payments || []).length})
                </TabsTrigger>
                <TabsTrigger value="activity" data-testid="tab-activity">Activity ({auditLog.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 text-sm">
                {/* Control toolbar */}
                <div className="flex flex-wrap gap-2 pb-3 border-b">
                  <Button size="sm" variant="outline" disabled={!canPay || actionBusy} onClick={copyPaymentLink} data-testid="admin-invoice-copy-link-btn">
                    <LinkIcon className="w-3.5 h-3.5 mr-1" /> Copy Client Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-teal-700 border-teal-200 hover:bg-teal-50"
                    disabled={!canPay || actionBusy}
                    onClick={openGuestLink}
                    data-testid="admin-invoice-guest-link-btn"
                    title="Generate a tokenized guest link that works without login"
                  >
                    <LinkIcon className="w-3.5 h-3.5 mr-1" /> Guest Payment Link
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    disabled={!canPay || actionBusy}
                    onClick={openExternal}
                    data-testid="admin-invoice-mark-paid-externally-btn"
                    title={canPay ? 'Record Check / Wire / ACH / Cash payment' : 'Invoice already paid or voided'}
                  >
                    <Banknote className="w-3.5 h-3.5 mr-1" /> Mark Paid Externally
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`/client/invoices/${detail.id}?as_client=${encodeURIComponent(detail.client_id)}`, '_blank', 'noopener')} data-testid="admin-invoice-preview-btn">
                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview as Client
                  </Button>
                  <Button size="sm" variant="outline" disabled={!canEdit || actionBusy} onClick={openEdit} data-testid="admin-invoice-edit-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" disabled={!canCredit || actionBusy} onClick={openCredit} data-testid="admin-invoice-credit-btn" title={canCredit ? '' : 'Credit notes are only for paid invoices'}>
                    <FileMinus className="w-3.5 h-3.5 mr-1" /> Issue Credit Note
                  </Button>
                  <Button size="sm" variant="outline" disabled={actionBusy} onClick={openNote} data-testid="admin-invoice-add-note-btn">
                    <StickyNote className="w-3.5 h-3.5 mr-1" /> Add Note
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" disabled={!canVoid || actionBusy} onClick={openVoid} data-testid="admin-invoice-cancel-btn">
                    <Ban className="w-3.5 h-3.5 mr-1" /> Cancel / Void Invoice
                  </Button>
                  {/* Iter-328 Phase 1 — Native Staff Payments */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                    onClick={openFunding}
                    disabled={actionBusy}
                    data-testid="admin-invoice-mark-funding-btn"
                    title="Mark client-funding-company deposit received for this invoice"
                  >
                    <Banknote className="w-3.5 h-3.5 mr-1" /> Mark Funding Received
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open('/admin/staff-payments', '_blank', 'noopener')}
                    data-testid="admin-invoice-open-staff-payments-btn"
                  >
                    <DollarSign className="w-3.5 h-3.5 mr-1" /> Contractor Payments
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-gray-500">Client</p>
                    <p className="font-medium text-gray-900">{detail.client_company_name || detail.client_company || detail.client_contact_name || detail.client_name || detail.client_email || '—'}</p>
                    {detail.client_company_name && detail.client_contact_name && (
                      <p className="text-xs text-gray-500">attn: {detail.client_contact_name}</p>
                    )}
                    {detail.client_email && <p className="text-xs text-gray-500">{detail.client_email}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500">Status</p>
                    <Badge className={`border text-[10px] ${(statusLabels[(detail.payment_status||'').toLowerCase()] || statusLabels.unpaid).cls}`}>
                      {(statusLabels[(detail.payment_status||'').toLowerCase()] || statusLabels.unpaid).text}
                    </Badge>
                  </div>
                </div>

                {detail.payment_status === 'cancelled' && detail.void_reason && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
                    <p className="font-medium mb-0.5">Void reason</p>
                    <p>{detail.void_reason}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div><p className="text-gray-500">Invoice date</p><p>{formatDateMMDDYYYY(detail.invoice_date) || '—'}</p></div>
                  <div><p className="text-gray-500">Due date</p><p>{formatDateMMDDYYYY(detail.due_date) || '—'}</p></div>
                  {detail.venue_name && <div><p className="text-gray-500">Venue</p><p>{detail.venue_name}</p></div>}
                  {detail.shift_date && <div><p className="text-gray-500">Event date</p><p>{formatDateMMDDYYYY(detail.shift_date)}</p></div>}
                </div>

                {Array.isArray(detail.line_items) && detail.line_items.length > 0 && (
                  <div className="border rounded overflow-hidden" data-testid="invoice-line-items-table">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50"><tr>
                        <th className="text-left p-2">Event / Date / Venue</th>
                        <th className="text-left p-2">Talent</th>
                        <th className="text-left p-2">Role</th>
                        <th className="text-right p-2">Hours</th>
                        <th className="text-right p-2">Rate</th>
                        <th className="text-right p-2">Amount</th>
                      </tr></thead>
                      <tbody>
                        {(() => {
                          // Iter-224f — when the invoice is consolidated, line
                          // items carry `day_group` + `day_subtotal` so we can
                          // render Day 1 / Day 2 / Day 3 sub-headers with daily
                          // subtotal rows.
                          const items = detail.line_items;
                          const isConsolidated = items.some((it) => it.day_group);
                          if (!isConsolidated) {
                            return items.map((li, i) => {
                              const ev = (li.event_name || '').trim();
                              const dt = (li.date_display || li.date || '').trim();
                              const vn = (li.venue_name || detail.venue_name || '').trim();
                              const metaBits = [dt, vn].filter(Boolean).join(' · ');
                              return (
                                <tr key={i} className="border-t align-top" data-testid={`invoice-line-row-${i}`}>
                                  <td className="p-2">
                                    <div className="font-medium text-gray-900">{ev || li.description?.split(' — ')[0] || '—'}</div>
                                    {metaBits && <div className="text-[11px] text-gray-500 mt-0.5">{metaBits}</div>}
                                  </td>
                                  <td className="p-2 text-gray-800">{li.talent_name || '—'}</td>
                                  <td className="p-2 text-gray-700">{li.role_name || '—'}</td>
                                  <td className="p-2 text-right">{li.hours != null ? Number(li.hours).toFixed(2) : (li.quantity ?? '—')}</td>
                                  <td className="p-2 text-right">${Number(li.rate ?? li.unit_price ?? 0).toFixed(2)}</td>
                                  <td className="p-2 text-right font-medium">${Number(li.amount ?? li.total_price ?? 0).toFixed(2)}</td>
                                </tr>
                              );
                            });
                          }
                          // Group by day_index, preserving insertion order.
                          const groups = new Map();
                          items.forEach((li, i) => {
                            const key = li.day_index || li.day_group || '?';
                            if (!groups.has(key)) {
                              groups.set(key, { label: li.day_group, items: [], subtotal: li.day_subtotal || 0 });
                            }
                            groups.get(key).items.push({ ...li, _idx: i });
                          });
                          const rows = [];
                          let dayCounter = 0;
                          for (const [key, grp] of groups.entries()) {
                            dayCounter += 1;
                            rows.push(
                              <tr key={`hdr-${key}`} className="bg-cyan-50/60 border-t border-cyan-200" data-testid={`invoice-day-header-${dayCounter}`}>
                                <td colSpan={6} className="p-2 font-semibold text-cyan-800">
                                  Day {dayCounter} · {grp.label}
                                </td>
                              </tr>
                            );
                            grp.items.forEach((li) => {
                              const ev = (li.event_name || '').trim();
                              const vn = (li.venue_name || detail.venue_name || '').trim();
                              rows.push(
                                <tr key={`row-${li._idx}`} className="border-t align-top" data-testid={`invoice-line-row-${li._idx}`}>
                                  <td className="p-2">
                                    <div className="font-medium text-gray-900">{ev || '—'}</div>
                                    {vn && <div className="text-[11px] text-gray-500 mt-0.5">{vn}</div>}
                                  </td>
                                  <td className="p-2 text-gray-800">{li.talent_name || '—'}</td>
                                  <td className="p-2 text-gray-700">{li.role_name || '—'}</td>
                                  <td className="p-2 text-right">{li.hours != null ? Number(li.hours).toFixed(2) : (li.quantity ?? '—')}</td>
                                  <td className="p-2 text-right">${Number(li.rate ?? li.unit_price ?? 0).toFixed(2)}</td>
                                  <td className="p-2 text-right font-medium">${Number(li.amount ?? li.total_price ?? 0).toFixed(2)}</td>
                                </tr>
                              );
                            });
                            rows.push(
                              <tr key={`sub-${key}`} className="bg-gray-50" data-testid={`invoice-day-subtotal-${dayCounter}`}>
                                <td colSpan={5} className="p-2 text-right text-gray-600 font-medium">Daily Subtotal</td>
                                <td className="p-2 text-right font-semibold">${Number(grp.subtotal || 0).toFixed(2)}</td>
                              </tr>
                            );
                          }
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="border-t pt-3 space-y-1">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>${(parseFloat(detail.subtotal || 0)).toFixed(2)}</span></div>
                  <div className="flex justify-between text-base font-semibold"><span>Total</span><span className="text-cyan-700">${(parseFloat(detail.total_amount || 0)).toFixed(2)}</span></div>
                  {parseFloat(detail.amount_paid || 0) > 0 && (
                    <div className="flex justify-between text-emerald-700"><span>Paid</span><span>${(parseFloat(detail.amount_paid)).toFixed(2)}</span></div>
                  )}
                  {parseFloat(detail.amount_due || 0) > 0 && (
                    <div className="flex justify-between text-sky-700"><span>Due</span><span>${(parseFloat(detail.amount_due)).toFixed(2)}</span></div>
                  )}
                  {detail.payment_structure === 'deposit' && detail.deposit_amount && (
                    <div className="flex justify-between text-xs text-amber-700 pt-1">
                      <span>Deposit ({detail.deposit_paid ? 'paid' : 'due'})</span>
                      <span>${parseFloat(detail.deposit_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {detail.payment_structure === 'deposit' && detail.balance_due_amount && (
                    <div className="flex justify-between text-xs text-amber-700">
                      <span>Balance due {formatDateMMDDYYYY(detail.final_payment_due_date)}</span>
                      <span>${parseFloat(detail.balance_due_amount).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {detail.notes && (
                  <div className="bg-gray-50 rounded p-3">
                    <p className="text-xs font-medium text-gray-600 mb-1">Notes</p>
                    <p className="text-xs text-gray-700">{detail.notes}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="payments" className="space-y-2" data-testid="payments-tab-content">
                {(!detail.payments || detail.payments.length === 0) ? (
                  <p className="text-sm text-gray-500 py-6 text-center flex flex-col items-center gap-2">
                    <Wallet className="w-6 h-6 text-gray-300" /> No payments recorded yet.
                  </p>
                ) : (
                  <div className="border rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50"><tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Method</th>
                        <th className="text-left p-2">Reference</th>
                        <th className="text-right p-2">Amount</th>
                      </tr></thead>
                      <tbody>
                        {detail.payments.map((p, i) => (
                          <tr key={p.id || i} className="border-t" data-testid={`payment-row-${i}`}>
                            <td className="p-2 text-gray-700">{p.payment_date ? formatDateMMDDYYYY(p.payment_date) : '—'}</td>
                            <td className="p-2 text-gray-800 capitalize">{p.payment_method || '—'}</td>
                            <td className="p-2 text-gray-600">{p.reference || '—'}</td>
                            <td className="p-2 text-right font-medium text-emerald-700">${Number(p.amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {detail.internal_notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
                    <p className="text-xs font-medium text-amber-800 mb-1 flex items-center gap-1">
                      <StickyNote className="w-3 h-3" /> Internal notes
                    </p>
                    <pre className="text-[11px] text-amber-900 whitespace-pre-wrap font-sans" data-testid="invoice-internal-notes">{detail.internal_notes}</pre>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="activity" className="space-y-2">
                {auditLog.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center flex flex-col items-center gap-2">
                    <History className="w-6 h-6 text-gray-300" /> No activity yet.
                  </p>
                ) : auditLog.map((a) => (
                  <div key={a.id} className="border rounded p-3" data-testid={`audit-row-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-gray-900 capitalize">{a.action.replace(/_/g, ' ')}</p>
                      <span className="text-[10px] text-gray-500">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-[11px] text-gray-600 mt-0.5">by {a.actor_email || 'system'}</p>
                    {a.reason && <p className="text-xs text-gray-700 mt-1 italic">"{a.reason}"</p>}
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* ─────── Void Confirmation ─────── */}
      <Dialog open={voidDialog} onOpenChange={setVoidDialog}>
        <DialogContent className="max-w-md bg-white" data-testid="void-confirm-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="w-4 h-4" /> Void Invoice</DialogTitle>
          <DialogDescription className="text-xs text-red-600">
            Voiding marks this invoice as permanently cancelled. This cannot be undone.
          </DialogDescription></DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            This will cancel <strong>{detail?.invoice_number}</strong>
            {detail?.payment_structure === 'deposit' && !detail?.parent_invoice_id && ' and any linked balance invoice'}.
            <br />This cannot be undone.
          </div>
          <div>
            <Label htmlFor="void-reason">Reason (required)</Label>
            <Textarea id="void-reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g., Client cancelled event, duplicate booking…" className="mt-1" data-testid="void-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidDialog(false)} disabled={actionBusy} data-testid="void-cancel-btn">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={submitVoid} disabled={actionBusy} data-testid="void-confirm-btn">
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Void Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────── Edit / Adjust ─────── */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit Invoice</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Update line items, terms, or notes. Changes are tracked in the invoice history.
          </DialogDescription></DialogHeader>
          <div className="space-y-3">
            {editLineItems.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  {i === 0 && <Label className="text-[11px]">Description</Label>}
                  <Input value={li.description} onChange={(e) => updateLineItem(i, 'description', e.target.value)} data-testid={`edit-desc-${i}`} />
                </div>
                <div className="col-span-2">
                  {i === 0 && <Label className="text-[11px]">Qty</Label>}
                  <Input type="number" step="0.01" value={li.quantity} onChange={(e) => updateLineItem(i, 'quantity', e.target.value)} data-testid={`edit-qty-${i}`} />
                </div>
                <div className="col-span-2">
                  {i === 0 && <Label className="text-[11px]">Rate</Label>}
                  <Input type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLineItem(i, 'unit_price', e.target.value)} data-testid={`edit-rate-${i}`} />
                </div>
                <div className="col-span-2">
                  {i === 0 && <Label className="text-[11px]">Amount</Label>}
                  <p className="h-9 flex items-center text-sm">${((parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0)).toFixed(2)}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeLineItem(i)} className="col-span-1 text-red-600" data-testid={`edit-remove-${i}`}>✕</Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addLineItem} data-testid="edit-add-line">+ Add line item</Button>
            <div className="text-right border-t pt-2">
              <p className="text-sm font-semibold">New Total: <span className="text-cyan-700">${editTotal.toFixed(2)}</span></p>
            </div>
            <div>
              <Label htmlFor="edit-notes">Internal notes (optional)</Label>
              <Textarea id="edit-notes" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} className="mt-1" data-testid="edit-notes-input" />
            </div>
            <div>
              <Label htmlFor="edit-reason">Reason for adjustment (required)</Label>
              <Textarea id="edit-reason" value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="e.g., Reduced hours, extra role added…" className="mt-1" data-testid="edit-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)} disabled={actionBusy} data-testid="edit-cancel-btn">Cancel</Button>
            <Button onClick={submitEdit} disabled={actionBusy} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="edit-submit-btn">
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────── Credit Note ─────── */}
      <Dialog open={creditDialog} onOpenChange={setCreditDialog}>
        <DialogContent className="max-w-md bg-white" data-testid="credit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-purple-700"><FileMinus className="w-4 h-4" /> Issue Credit Note</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Issue a credit note against this invoice. Reduces the amount due and is reflected on the client's statement.
          </DialogDescription></DialogHeader>
          <p className="text-xs text-gray-600">Paid amount: <strong>${parseFloat(detail?.amount_paid || 0).toFixed(2)}</strong>. Credit cannot exceed this.</p>
          <div>
            <Label htmlFor="credit-amt">Credit amount ($)</Label>
            <Input id="credit-amt" type="number" step="0.01" min="0.01" max={parseFloat(detail?.amount_paid || 0)} value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} className="mt-1" data-testid="credit-amount-input" />
          </div>
          <div>
            <Label htmlFor="credit-reason">Reason (required)</Label>
            <Input id="credit-reason" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} className="mt-1" data-testid="credit-reason-input" />
          </div>
          <div>
            <Label htmlFor="credit-notes">Notes (optional)</Label>
            <Textarea id="credit-notes" value={creditNotes} onChange={(e) => setCreditNotes(e.target.value)} className="mt-1" data-testid="credit-notes-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialog(false)} disabled={actionBusy} data-testid="credit-cancel-btn">Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={submitCredit} disabled={actionBusy} data-testid="credit-submit-btn">
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Issue Credit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────── Mark Paid Externally (Iter-223b) ─────── */}
      <Dialog open={extDialog} onOpenChange={setExtDialog}>
        <DialogContent className="max-w-md bg-white" data-testid="external-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Banknote className="w-4 h-4" /> Mark Paid Externally
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Record a payment received outside Stripe (check, wire, ACH, cash, or other channel).
              Updates the AR balance and writes a payment audit row.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-600">
              Invoice <strong>{detail?.invoice_number}</strong> · amount due:{' '}
              <strong>${parseFloat(detail?.amount_due || 0).toFixed(2)}</strong>
            </p>
            <div>
              <Label className="text-[11px]">Payment method</Label>
              <div className="mt-1 grid grid-cols-3 gap-1.5">
                {[
                  { id: 'check', label: 'Check' },
                  { id: 'funding_company', label: 'Funding Company' },
                  { id: 'wire', label: 'Wire' },
                  { id: 'ach', label: 'ACH' },
                  { id: 'cash', label: 'Cash' },
                  { id: 'venmo', label: 'Venmo' },
                  { id: 'other', label: 'Other' },
                ].map((m) => (
                  <Button
                    key={m.id}
                    type="button"
                    size="sm"
                    variant={extMethod === m.id ? 'default' : 'outline'}
                    onClick={() => setExtMethod(m.id)}
                    className={extMethod === m.id ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                    data-testid={`ext-method-${m.id}`}
                  >
                    {m.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ext-amount">Amount ($)</Label>
                <Input
                  id="ext-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={parseFloat(detail?.amount_due || 0)}
                  value={extAmount}
                  onChange={(e) => setExtAmount(e.target.value)}
                  className="mt-1"
                  data-testid="ext-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="ext-paid-on">Paid on</Label>
                <Input
                  id="ext-paid-on"
                  type="date"
                  value={extPaidOn}
                  onChange={(e) => setExtPaidOn(e.target.value)}
                  className="mt-1"
                  data-testid="ext-paid-on-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ext-ref">Reference number (check #, wire confirmation, ACH trace…)</Label>
              <Input
                id="ext-ref"
                value={extReference}
                onChange={(e) => setExtReference(e.target.value)}
                placeholder="e.g., Check #4471 or ACH-7831924"
                className="mt-1"
                data-testid="ext-reference-input"
              />
            </div>
            <div>
              <Label htmlFor="ext-notes">Admin notes (optional)</Label>
              <Textarea
                id="ext-notes"
                value={extNotes}
                onChange={(e) => setExtNotes(e.target.value)}
                placeholder="Internal context — who paid, who confirmed, deposit channel, etc."
                className="mt-1"
                data-testid="ext-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtDialog(false)} disabled={actionBusy} data-testid="ext-cancel-btn">Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={submitExternal}
              disabled={actionBusy}
              data-testid="ext-submit-btn"
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────── Iter-227g — Bulk Mark Paid Externally ─────── */}
      <Dialog open={bulkDialog} onOpenChange={(o) => { if (!o) { setBulkDialog(false); setBulkResult(null); } }}>
        <DialogContent className="max-w-lg bg-white max-h-[88vh] overflow-y-auto" data-testid="bulk-paid-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Banknote className="w-5 h-5" />
              Bulk: Mark {selectedIds.size} Invoices Paid Externally
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              One shared payment method, reference, and date will be applied to every selected invoice.
              Each invoice will be settled for its full outstanding balance. An audit row is written per
              invoice with a shared <code>bulk_operation_id</code>.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bulk-method">Payment method</Label>
                  <select
                    id="bulk-method"
                    value={bulkMethod}
                    onChange={(e) => setBulkMethod(e.target.value)}
                    className="mt-1 w-full border rounded-md h-9 px-2 text-sm"
                    data-testid="bulk-method-select"
                  >
                    <option value="ach">ACH</option>
                    <option value="check">Check</option>
                    <option value="funding_company">Funding Company</option>
                    <option value="wire">Wire</option>
                    <option value="cash">Cash</option>
                    <option value="venmo">Venmo</option>
                    <option value="paypal">PayPal</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="bulk-paid-on">Date paid</Label>
                  <Input
                    id="bulk-paid-on"
                    type="date"
                    value={bulkPaidOn}
                    onChange={(e) => setBulkPaidOn(e.target.value)}
                    className="mt-1"
                    data-testid="bulk-paid-on"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor="bulk-ref">Reference number (shared)</Label>
                <Input
                  id="bulk-ref"
                  value={bulkReference}
                  onChange={(e) => setBulkReference(e.target.value)}
                  placeholder="e.g., ACH-7831924 or Check #4471"
                  className="mt-1"
                  data-testid="bulk-reference"
                />
              </div>
              <div className="mt-3">
                <Label htmlFor="bulk-notes">Admin notes (optional)</Label>
                <Textarea
                  id="bulk-notes"
                  value={bulkNotes}
                  onChange={(e) => setBulkNotes(e.target.value)}
                  placeholder="Why this batch? Useful for the audit trail."
                  className="mt-1"
                  rows={2}
                  data-testid="bulk-notes"
                />
              </div>
              <div className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-2">
                {selectedIds.size} invoice(s) will be settled in this batch.
                Already-paid or voided invoices were excluded from selection.
              </div>
              <DialogFooter className="mt-3">
                <Button variant="outline" onClick={() => setBulkDialog(false)} disabled={bulkBusy} data-testid="bulk-cancel-btn">Cancel</Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={submitBulk}
                  disabled={bulkBusy || !bulkPaidOn}
                  data-testid="bulk-submit-btn"
                >
                  {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Settle ${selectedIds.size} Invoice(s)`}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="bulk-result-summary">
                <p className="font-semibold text-emerald-800">
                  {bulkResult.succeeded}/{bulkResult.requested} settled — ${bulkResult.total_settled.toFixed(2)}
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Audit ID: <code className="text-[10px]">{bulkResult.bulk_operation_id}</code>
                </p>
                {bulkResult.failed > 0 && (
                  <p className="text-xs text-rose-700 mt-1">{bulkResult.failed} failed — see below</p>
                )}
              </div>
              <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-gray-100 border rounded-md">
                {bulkResult.results.map((r) => (
                  <div key={r.invoice_id} className="px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid={`bulk-result-${r.invoice_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900">{r.invoice_number || r.invoice_id.slice(0, 8)}</p>
                      {!r.ok && <p className="text-rose-700">{r.error}</p>}
                    </div>
                    {r.ok ? (
                      <span className="text-emerald-700 font-medium">
                        ${(r.amount_applied || 0).toFixed(2)} → {r.new_status}
                      </span>
                    ) : (
                      <span className="text-rose-700 font-medium">failed</span>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter className="mt-3">
                <Button
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  onClick={() => { setBulkDialog(false); setBulkResult(null); clearSelection(); }}
                  data-testid="bulk-done-btn"
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─────── Add Note (Iter-223c) ─────── */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent className="max-w-md bg-white" data-testid="add-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <StickyNote className="w-4 h-4" /> Add Internal Note
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Append a private admin note to this invoice's audit trail. Visible to admins only.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="note-text">Note</Label>
            <Textarea
              id="note-text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="e.g., Client confirmed check #4471 mailed 5/20. ETA 7 business days."
              className="mt-1"
              rows={4}
              data-testid="add-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)} disabled={actionBusy} data-testid="add-note-cancel-btn">Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={submitNote}
              disabled={actionBusy || !noteText.trim()}
              data-testid="add-note-submit-btn"
            >
              {actionBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────── Iter-327: Universal (Guest) Payment Link ─────── */}
      <Dialog open={guestLinkDialog} onOpenChange={setGuestLinkDialog}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="guest-payment-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <LinkIcon className="w-4 h-4" /> Guest Payment Link
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Share a tokenized Stripe Checkout link with any recipient — no login required.
              Fully paid invoices auto-disable the link.
            </DialogDescription>
          </DialogHeader>

          {guestLinkLoading && !guestLink && (
            <div className="py-8 flex items-center justify-center text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
            </div>
          )}

          {!guestLinkLoading && (!guestLink || guestLink.status === 'not_generated') && (
            <div className="space-y-3">
              <div className="text-sm text-gray-600">No payment link yet. Generate one below.</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="gl-expiry">Expires in (days)</Label>
                  <Input
                    id="gl-expiry"
                    type="number"
                    min="1"
                    max="365"
                    value={genExpiryDays}
                    onChange={(e) => setGenExpiryDays(parseInt(e.target.value || '30', 10))}
                    disabled={genNoExpiry}
                    className="mt-1"
                    data-testid="gl-expiry-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={genNoExpiry}
                      onChange={(e) => setGenNoExpiry(e.target.checked)}
                      data-testid="gl-no-expiry-checkbox"
                    />
                    Never expires
                  </label>
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={genPassFees}
                  onChange={(e) => setGenPassFees(e.target.checked)}
                  data-testid="gl-pass-fees-checkbox"
                  className="mt-0.5"
                />
                <span>
                  <span className="font-medium">Add processing fees on top</span>
                  <span className="block text-xs text-gray-500">Guest pays invoice amount + 2.9% + $0.30 Stripe fee. Leave unchecked to absorb the fee.</span>
                </span>
              </label>
              <Button
                onClick={generateGuestLink}
                disabled={guestLinkLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="gl-generate-btn"
              >
                {guestLinkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
                Generate Payment Link
              </Button>
            </div>
          )}

          {!guestLinkLoading && guestLink && guestLink.status !== 'not_generated' && (
            <div className="space-y-4">
              <div className="rounded-md border border-teal-200 bg-teal-50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <Badge className={`border text-[10px] ${
                    guestLink.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                    guestLink.status === 'paid' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                    guestLink.status === 'expired' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                    'bg-gray-100 text-gray-700 border-gray-200'
                  }`} data-testid="gl-status-badge">
                    {guestLink.status.toUpperCase()}
                  </Badge>
                  <span className="text-xs text-gray-600">
                    ${Number(guestLink.amount_paid || 0).toFixed(2)} of ${Number(guestLink.total_amount || 0).toFixed(2)} paid
                  </span>
                </div>
                <div className="text-xs text-gray-500 mb-1">Shareable URL</div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={guestLink.url || ''} className="text-xs font-mono" data-testid="gl-url-input" />
                  <Button size="sm" variant="outline" onClick={copyGuestLink} data-testid="gl-copy-btn">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                  <div>Expires: {guestLink.expires_at ? new Date(guestLink.expires_at).toLocaleString() : 'never'}</div>
                  <div>Fees: {guestLink.pass_fees ? 'Added on top' : 'Absorbed'}</div>
                  <div>Remaining: ${Number(guestLink.amount_remaining || 0).toFixed(2)}</div>
                  <div>{guestLink.last_sent_at ? `Last sent ${new Date(guestLink.last_sent_at).toLocaleString()}` : 'Not yet sent'}</div>
                </div>
              </div>

              {(guestLink.status === 'active') && (
                <div className="space-y-3 border-t pt-3">
                  <div className="text-sm font-semibold">Send link to recipient</div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={sendChannel === 'email' ? 'default' : 'outline'}
                      onClick={() => { setSendChannel('email'); setSendTo(detail?.client_email || ''); }}
                      data-testid="gl-channel-email-btn"
                    >
                      Email
                    </Button>
                    <Button
                      size="sm"
                      variant={sendChannel === 'sms' ? 'default' : 'outline'}
                      onClick={() => { setSendChannel('sms'); setSendTo(''); }}
                      data-testid="gl-channel-sms-btn"
                    >
                      SMS
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Recipient name (optional)</Label>
                      <Input
                        value={sendGuestName}
                        onChange={(e) => setSendGuestName(e.target.value)}
                        placeholder="Jane Doe"
                        className="mt-1"
                        data-testid="gl-send-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{sendChannel === 'email' ? 'Email' : 'Mobile number'}</Label>
                      <Input
                        value={sendTo}
                        onChange={(e) => setSendTo(e.target.value)}
                        placeholder={sendChannel === 'email' ? 'you@example.com' : '+1 555 123 4567'}
                        className="mt-1"
                        data-testid="gl-send-to-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Custom message (optional)</Label>
                    <Textarea
                      value={sendMessage}
                      onChange={(e) => setSendMessage(e.target.value)}
                      placeholder="Add a note (e.g., 'Thanks again for the wedding — final balance below.')"
                      className="mt-1 text-sm"
                      rows={2}
                      data-testid="gl-send-message-input"
                    />
                  </div>
                  <Button
                    onClick={sendGuestLink}
                    disabled={sendBusy || !sendTo.trim()}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                    data-testid="gl-send-btn"
                  >
                    {sendBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Send via {sendChannel === 'email' ? 'Email' : 'SMS'}
                  </Button>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-3 border-t">
                <Button size="sm" variant="outline" onClick={regenerateGuestLink} disabled={guestLinkLoading} data-testid="gl-regenerate-btn">
                  Regenerate
                </Button>
                <Button size="sm" variant="outline" onClick={extendGuestLink} disabled={guestLinkLoading} data-testid="gl-extend-btn">
                  Extend Expiration
                </Button>
                {guestLink.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    onClick={disableGuestLink}
                    disabled={guestLinkLoading}
                    data-testid="gl-disable-btn"
                  >
                    Disable
                  </Button>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setGuestLinkDialog(false)} data-testid="gl-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─────── Iter-328 Phase 1: Mark Funding Received ─────── */}
      <Dialog open={fundingDialog} onOpenChange={setFundingDialog}>
        <DialogContent className="max-w-md bg-white" data-testid="mark-funding-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <Banknote className="w-4 h-4" /> Mark Funding Received
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Record that the client&apos;s funding company deposited the invoice payment into your iEvntür business bank account.
              This unlocks the ability to build a staff payment batch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="funding-amount">Deposit amount (USD)</Label>
              <Input id="funding-amount" type="number" inputMode="decimal" min="0.01" step="0.01"
                     value={fundingAmount} onChange={(e) => setFundingAmount(e.target.value)}
                     placeholder="0.00" data-testid="funding-amount-input" />
            </div>
            <div>
              <Label htmlFor="funding-ref">Reference (wire #, check #, ACH memo)</Label>
              <Input id="funding-ref" value={fundingReference} onChange={(e) => setFundingReference(e.target.value)}
                     placeholder="e.g., WIRE-2026-04-12" data-testid="funding-reference-input" />
            </div>
            <div>
              <Label htmlFor="funding-notes">Notes (optional)</Label>
              <Textarea id="funding-notes" rows={2} value={fundingNotes} onChange={(e) => setFundingNotes(e.target.value)} data-testid="funding-notes-input" />
            </div>
            {batchResult && (
              <div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
                <div className="font-medium">Payment batch created</div>
                <div className="text-xs mt-1">
                  {batchResult.line_count || 0} talent line(s) · ${(batchResult.total_gross || 0).toFixed(2)} gross
                </div>
                {(batchResult.blockers || []).length > 0 && (
                  <div className="text-xs mt-2 text-amber-700">
                    ⚠️ {batchResult.blockers.length} talent blocked (Stripe not ready).
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => window.open('/admin/staff-payments', '_blank', 'noopener')}
                  data-testid="funding-open-batch-btn"
                >
                  Open Contractor Payments
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFundingDialog(false)} disabled={fundingBusy}>Close</Button>
            {!batchResult && (
              <Button onClick={submitFunding} disabled={fundingBusy || !fundingAmount} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="funding-submit-btn">
                {fundingBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Record Funding + Build Batch
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInvoices;
